import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AgremoCreditBlock,
  AgremoCreditBlockCreateRequest,
  AgremoCreditBlockUpdateRequest,
  AgremoAnalysis,
  AgremoAnalysisDetail,
  AgremoAnalysisFilters,
  AgremoUsageStats,
  AgremoHealthStatus,
  AgremoMapStatusCode,
  AgremoCameraType,
  AgremoLayerType,
  AgremoLayerStatusCode,
  AgremoAnalysisTypeCodebook,
  AgremoProcessPendingResult,
  PaginatedCreditsResponse,
  PaginatedAnalysesResponse
} from '../models/agremo.model';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Agremo Admin Service
 *
 * Service for managing Agremo credits and viewing analyses in the admin portal.
 * Communicates with /admin/agremo/* endpoints.
 */
@Injectable({
  providedIn: 'root'
})
export class AgremoAdminService {
  private readonly apiUrl = `${environment.apiUrl}/admin/agremo`;
  private readonly orgApiUrl = `${environment.apiUrl}/admin/organizations`;

  constructor(private http: HttpClient) {}

  // ============================================
  // CREDIT BLOCK MANAGEMENT
  // ============================================

  /**
   * List all credit blocks
   */
  listCredits(): Observable<PaginatedCreditsResponse> {
    return this.http.get<ApiResponse<any>>(`${this.apiUrl}/credits`).pipe(
      map((response) => {
        if (response.success && response.data) {
          const rawBlocks: AgremoCreditBlock[] =
            response.data.blocks || response.data.credits || [];

          // Normalize blocks: pre-2026-05-20 Hub builds omit creditType
          // entirely. Default missing values to 'analysis' (the legacy
          // "1 credit = 1 analysis" semantic) so downstream branching
          // code and the dual-unit display can rely on creditType being
          // defined. Post-migration responses pass through unchanged.
          const credits: AgremoCreditBlock[] = rawBlocks.map((b) =>
            b.creditType ? b : { ...b, creditType: 'analysis' as const },
          );

          // Per-field fallback: each unit total falls back independently
          // when the Hub omits it. A partial payload (e.g. Hub sends
          // `availableAnalyses` but not `availableAcres`) leaves the
          // missing fields as `undefined` so the component's `??` fallback
          // (computeAvailableAcres from the credit list) kicks in for
          // each absent field individually. A coarse "if any unit total
          // is present, default all missing fields to 0" would short-
          // circuit those fallbacks and display a misleading 0 against
          // a partial-payload response.
          //
          // For `availableAnalyses` specifically: if absent, fall back to
          // the legacy `totalAvailable` ONLY when the Hub actually sent it
          // — pre-migration every block IS analysis-typed, so legacy
          // `totalAvailable` IS the analysis-credit count by definition.
          // Critically: distinguish "Hub omitted totalAvailable" from
          // "Hub sent totalAvailable: 0." If we collapsed an omitted
          // totalAvailable to 0 (via `|| 0`) and then used that as the
          // availableAnalyses fallback, we'd lock the field to 0 and
          // suppress the component's compute-from-blocks fallback,
          // displaying a misleading 0 against analysis-typed blocks.
          const rawTotalAvailable: number | undefined =
            typeof response.data.totalAvailable === 'number'
              ? response.data.totalAvailable
              : undefined;
          return {
            credits,
            // `totalAvailable` itself is a REQUIRED number on the response
            // type; coerce missing to 0 here only for the wire shape, not
            // for derived fallbacks below.
            totalAvailable: rawTotalAvailable ?? 0,
            availableAcres: response.data.availableAcres,
            availableHectares: response.data.availableHectares,
            // Fall back to the raw API value (not the coerced 0) so the
            // component can compute from `credits` when both are absent.
            availableAnalyses: response.data.availableAnalyses ?? rawTotalAvailable,
            expiringBlockCount: response.data.expiringBlockCount || 0,
          } as PaginatedCreditsResponse;
        }
        return { credits: [], totalAvailable: 0, expiringBlockCount: 0 } as PaginatedCreditsResponse;
      })
    );
  }

  /**
   * Create a new credit block
   */
  createCredit(data: AgremoCreditBlockCreateRequest): Observable<AgremoCreditBlock> {
    return this.http.post<ApiResponse<AgremoCreditBlock>>(`${this.apiUrl}/credits`, data).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to create credit block');
      })
    );
  }

  /**
   * Update an existing credit block
   */
  updateCredit(id: string, data: AgremoCreditBlockUpdateRequest): Observable<AgremoCreditBlock> {
    return this.http.put<ApiResponse<AgremoCreditBlock>>(`${this.apiUrl}/credits/${id}`, data).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to update credit block');
      })
    );
  }

  /**
   * Archive a credit block (soft-delete)
   */
  archiveCredit(id: string): Observable<void> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/credits/${id}`).pipe(
      map((response) => {
        if (!response.success) {
          throw new Error(response.error || 'Failed to archive credit block');
        }
      })
    );
  }

  // ============================================
  // USAGE STATISTICS
  // ============================================

  /**
   * Get usage statistics
   */
  getUsageStats(): Observable<AgremoUsageStats> {
    return this.http.get<ApiResponse<AgremoUsageStats>>(`${this.apiUrl}/usage`).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        return {
          totalConsumed: 0,
          totalAvailable: 0,
          byOrganization: [],
          byAnalysisType: []
        };
      })
    );
  }

  // ============================================
  // ANALYSES
  // ============================================

  /**
   * List all analyses across all organizations with optional filters
   */
  listAnalyses(filters?: AgremoAnalysisFilters): Observable<PaginatedAnalysesResponse> {
    const params = new URLSearchParams();

    if (filters?.page !== undefined && filters.page !== null) {
      params.append('page', filters.page.toString());
    }
    if (filters?.limit !== undefined && filters.limit !== null) {
      params.append('limit', filters.limit.toString());
    }
    if (filters?.organizationId) {
      params.append('organizationId', filters.organizationId);
    }
    if (filters?.status && filters.status !== 'all') {
      params.append('status', filters.status);
    }
    if (filters?.startDate) {
      params.append('startDate', filters.startDate);
    }
    if (filters?.endDate) {
      params.append('endDate', filters.endDate);
    }

    const queryString = params.toString();
    const url = queryString ? `${this.apiUrl}/analyses?${queryString}` : `${this.apiUrl}/analyses`;

    return this.http.get<ApiResponse<PaginatedAnalysesResponse>>(url).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        return { analyses: [], total: 0, page: 1, pageSize: 20 };
      })
    );
  }

  /**
   * List analyses for a specific organization
   */
  listOrganizationAnalyses(organizationId: string, filters?: AgremoAnalysisFilters): Observable<PaginatedAnalysesResponse> {
    const params = new URLSearchParams();

    if (filters?.page !== undefined && filters.page !== null) {
      params.append('page', filters.page.toString());
    }
    if (filters?.limit !== undefined && filters.limit !== null) {
      params.append('limit', filters.limit.toString());
    }
    if (filters?.status && filters.status !== 'all') {
      params.append('status', filters.status);
    }
    if (filters?.startDate) {
      params.append('startDate', filters.startDate);
    }
    if (filters?.endDate) {
      params.append('endDate', filters.endDate);
    }

    const queryString = params.toString();
    const url = queryString
      ? `${this.orgApiUrl}/${organizationId}/agremo/analyses?${queryString}`
      : `${this.orgApiUrl}/${organizationId}/agremo/analyses`;

    return this.http.get<ApiResponse<PaginatedAnalysesResponse>>(url).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        return { analyses: [], total: 0, page: 1, pageSize: 20 };
      })
    );
  }

  /**
   * Get a single analysis with files and enrichment
   */
  getAnalysis(id: string): Observable<AgremoAnalysisDetail> {
    return this.http.get<ApiResponse<AgremoAnalysisDetail>>(`${this.apiUrl}/analyses/${id}`).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Analysis not found');
      })
    );
  }

  // ============================================
  // HEALTH CHECK
  // ============================================

  /**
   * Get map status codebook from Agremo API
   */
  getMapStatuses(): Observable<AgremoMapStatusCode[]> {
    return this.http.get<ApiResponse<AgremoMapStatusCode[]>>(`${this.apiUrl}/health/map-statuses`).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        return [];
      })
    );
  }

  /**
   * Get camera types codebook from Agremo API
   */
  getCameraTypes(): Observable<AgremoCameraType[]> {
    return this.http.get<ApiResponse<AgremoCameraType[]>>(`${this.apiUrl}/health/cameras`).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        return [];
      })
    );
  }

  /**
   * Get layer types codebook from Agremo API
   */
  getLayerTypes(): Observable<AgremoLayerType[]> {
    return this.http.get<ApiResponse<AgremoLayerType[]>>(`${this.apiUrl}/health/layer-types`).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        return [];
      })
    );
  }

  /**
   * Get analysis types from Agremo API
   */
  getAnalysisTypes(): Observable<AgremoAnalysisTypeCodebook[]> {
    return this.http.get<ApiResponse<AgremoAnalysisTypeCodebook[]>>(`${this.apiUrl}/health/analysis-types`).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        return [];
      })
    );
  }

  /**
   * Get layer statuses codebook from Agremo API
   */
  getLayerStatuses(): Observable<AgremoLayerStatusCode[]> {
    return this.http.get<ApiResponse<AgremoLayerStatusCode[]>>(`${this.apiUrl}/health/layer-statuses`).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        return [];
      })
    );
  }

  /**
   * Check Agremo API connectivity and credential validity
   */
  checkHealth(): Observable<AgremoHealthStatus> {
    return this.http.get<ApiResponse<AgremoHealthStatus>>(`${this.apiUrl}/health`).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        return {
          connected: false,
          error: response.error || 'Unknown error',
          checkedAt: new Date().toISOString()
        };
      })
    );
  }

  // ============================================
  // PROCESS PENDING
  // ============================================

  /**
   * Trigger processing of pending analyses
   */
  processPending(): Observable<AgremoProcessPendingResult> {
    return this.http.post<ApiResponse<AgremoProcessPendingResult>>(`${this.apiUrl}/process-pending`, {}).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to process pending analyses');
      })
    );
  }

  // ============================================
  // MAP RECHECK
  // ============================================

  /**
   * Recheck a failed map's readiness in Agremo
   */
  recheckMap(mapId: string): Observable<{ mapId: string; message: string }> {
    return this.http.post<ApiResponse<{ mapId: string; message: string }>>(
      `${this.apiUrl}/maps/${mapId}/recheck`, {}
    ).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to recheck map');
      })
    );
  }

  // ============================================
  // RETRY DOWNLOAD
  // ============================================

  /**
   * Retry downloading result files for a completed or failed analysis
   */
  retryDownload(analysisId: string): Observable<{ analysisId: string; message: string }> {
    return this.http.post<ApiResponse<{ analysisId: string; message: string }>>(
      `${this.apiUrl}/analyses/${analysisId}/retry-download`, {}
    ).pipe(
      map((response) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.error || 'Failed to retry download');
      })
    );
  }
}
